// liveFamily.js — ONE client-side invalidation pattern for every live family (P1, 2026-11-02)
//
// The server sends `entity_changed { family, ids, op }` — id-only, never a body
// (CLAUDE.md, "The WebSocket is a READ PATH"). App.js re-dispatches it as the
// window event `crm:entity_changed`, and every reconnect / seq gap / fallback
// poll as `crm:live_resync`. A page that shows a family subscribes here and
// REFETCHES the affected rows (or the list) through the REST route — which is
// what carries the scope and the redaction. Nothing on the page is patched
// from the frame itself, because the frame has nothing to patch with.
//
// Coalescing: a bulk operation sends many ids in a burst; a 1.5s tick unions
// them per family and fires onChange ONCE per family with the id list, or with
// null when any frame in the burst had no ids (the list changed — re-read
// page 1). `isEditing()` defers the flush while the page has a row open for
// editing, so a live refetch never overwrites a half-typed form; the tick
// retries until the editor closes.
//
// The pure core (createLiveQueue) has no React or DOM in it so the build gate
// can run it; the hooks are thin wrappers.

import { useEffect, useRef, useState } from "react";

export var LIVE_TICK_MS = 1500;

// createLiveQueue({ families, onChange(family, ids|null, why), isEditing(), setTimeout, clearTimeout })
export function createLiveQueue(opts) {
  var families = Array.isArray(opts.families) ? opts.families : [opts.families];
  var setT = opts.setTimeout || setTimeout, clearT = opts.clearTimeout || clearTimeout;
  var tick = typeof opts.tickMs === "number" ? opts.tickMs : LIVE_TICK_MS;
  var pending = {}, timer = null, closed = false;
  function flush() {
    timer = null;
    if (closed) return;
    if (typeof opts.isEditing === "function" && opts.isEditing()) { timer = setT(flush, tick); return; }
    var batch = pending; pending = {};
    Object.keys(batch).forEach(function (f) {
      var e = batch[f];
      try { opts.onChange(f, e.ids ? Object.keys(e.ids) : null, e.why); } catch (err) { /* a page's refetch must not break the queue */ }
    });
  }
  function queue(family, ids, why) {
    if (closed || families.indexOf(family) === -1) return;
    var e = pending[family] || (pending[family] = { ids: {}, why: why });
    if (ids === null || e.ids === null) e.ids = null;
    else ids.forEach(function (id) { e.ids[String(id)] = true; });
    if (!timer) timer = setT(flush, tick);
  }
  return {
    // an entity_changed frame's data
    push: function (data) {
      if (!data || !data.family) return;
      queue(data.family, (Array.isArray(data.ids) && data.ids.length) ? data.ids : null, data.op || "changed");
    },
    // a resync: every subscribed family re-reads its list
    resync: function (why) { families.forEach(function (f) { queue(f, null, why || "resync"); }); },
    pendingFamilies: function () { return Object.keys(pending); },
    close: function () { closed = true; if (timer) { clearT(timer); timer = null; } pending = {}; }
  };
}

// useLiveFamily("broker" | ["developer","closingCompany"], { onChange, isEditing })
export function useLiveFamily(families, handlers) {
  var key = (Array.isArray(families) ? families : [families]).join("|");
  var ref = useRef(handlers); ref.current = handlers;
  useEffect(function () {
    if (typeof window === "undefined") return;
    var q = createLiveQueue({
      families: key.split("|"),
      onChange: function (f, ids, why) { var h = ref.current; if (h && h.onChange) h.onChange(f, ids, why); },
      isEditing: function () { var h = ref.current; return !!(h && h.isEditing && h.isEditing()); }
    });
    var onEntity = function (e) { q.push(e && e.detail); };
    var onResync = function (e) { q.resync(e && e.detail && e.detail.why); };
    window.addEventListener("crm:entity_changed", onEntity);
    window.addEventListener("crm:live_resync", onResync);
    return function () {
      window.removeEventListener("crm:entity_changed", onEntity);
      window.removeEventListener("crm:live_resync", onResync);
      q.close();
    };
  }, [key]);
}

// A counter that bumps once per coalesced change on the family — drop it into
// an existing fetch effect's dependency list and the page re-reads the list.
export function useLiveRev(families) {
  var [rev, setRev] = useState(0);
  useLiveFamily(families, { onChange: function () { setRev(function (v) { return v + 1; }); } });
  return rev;
}
